import React from 'react';
import { X, Bell, AlertTriangle, Info, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AnnouncementPopup = ({ announcement, onDismiss, theme = 'light' }) => {
  const navigate = useNavigate();
  
  if (!announcement) return null;
  
  const handleAction = () => {
    if (announcement.action_link) {
      // Si es un link interno (empieza con /)
      if (announcement.action_link.startsWith('/')) {
        navigate(announcement.action_link);
        onDismiss();
      } else {
        // Link externo
        window.open(announcement.action_link, '_blank');
      }
    }
  };

  const getIcon = () => {
    switch (announcement.icon) {
      case 'alert-triangle':
        return <AlertTriangle className="w-8 h-8" />;
      case 'info':
        return <Info className="w-8 h-8" />;
      case 'check-circle':
        return <CheckCircle className="w-8 h-8" />;
      case 'bell':
      default:
        return <Bell className="w-8 h-8" />;
    }
  };

  const getColors = () => {
    const isDark = theme === 'dark';
    
    switch (announcement.type) {
      case 'warning':
        return {
          bg: isDark ? 'bg-yellow-50' : 'bg-yellow-50',
          border: 'border-yellow-500',
          icon: 'text-yellow-600',
          title: isDark ? 'text-yellow-200' : 'text-yellow-900',
          text: isDark ? 'text-yellow-100' : 'text-yellow-800',
          button: 'bg-yellow-600 hover:bg-yellow-700'
        };
      case 'critical':
        return {
          bg: isDark ? 'bg-red-50' : 'bg-red-50',
          border: 'border-red-500',
          icon: 'text-red-600',
          title: isDark ? 'text-red-200' : 'text-red-900',
          text: isDark ? 'text-red-100' : 'text-red-800',
          button: 'bg-red-600 hover:bg-red-700'
        };
      case 'success':
        return {
          bg: isDark ? 'bg-green-50' : 'bg-green-50',
          border: 'border-green-500',
          icon: 'text-green-600',
          title: isDark ? 'text-green-200' : 'text-green-900',
          text: isDark ? 'text-green-100' : 'text-green-800',
          button: 'bg-green-600 hover:bg-green-700'
        };
      case 'info':
      default:
        return {
          bg: isDark ? 'bg-blue-50' : 'bg-blue-50',
          border: 'border-blue-500',
          icon: 'text-blue-600',
          title: isDark ? 'text-blue-200' : 'text-blue-900',
          text: isDark ? 'text-blue-100' : 'text-blue-800',
          button: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };

  const colors = getColors();
  const isPriority = announcement.priority >= 2;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 animate-fadeIn"
        onClick={onDismiss}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className={`${colors.bg} ${colors.border} border-l-4 rounded-lg shadow-2xl max-w-2xl w-full pointer-events-auto transform animate-slideUp`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className={`${colors.icon} ${isPriority ? 'animate-pulse' : ''}`}>
                {getIcon()}
              </div>
              <div className="flex-1">
                <h3 className={`text-2xl font-bold ${colors.title} mb-2`}>
                  {isPriority && '⚠️ '} {announcement.title}
                </h3>
                {announcement.priority === 3 && (
                  <span className="inline-block px-2 py-1 text-xs font-bold bg-red-600 text-white rounded mb-2">
                    URGENTE
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onDismiss}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark' 
                  ? 'hover:bg-gray-700 text-gray-400' 
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <div className={`${colors.text} whitespace-pre-wrap text-base leading-relaxed`}>
              {announcement.message}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-300">
              <p className="text-xs text-gray-500">
                {new Date(announcement.created_at).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <div className="flex gap-3">
                {announcement.action_link && announcement.action_text && (
                  <button
                    onClick={handleAction}
                    className={`flex items-center gap-2 px-6 py-2 ${colors.button} text-white rounded-lg font-semibold transition-colors`}
                  >
                    {announcement.action_text}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onDismiss}
                  className={`px-6 py-2 ${
                    announcement.action_link 
                      ? (theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')
                      : colors.button
                  } text-white rounded-lg font-semibold transition-colors`}
                >
                  {announcement.action_link ? 'Cerrar' : 'Entendido'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default AnnouncementPopup;
